// client/src/pages/AuctionDetailPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import Countdown from '../components/Countdown';
import {
  Container, Grid, Box, Typography, Chip, Button, TextField, Stack, Card, CardContent, Divider,
  List, ListItem, ListItemText, Alert, CircularProgress, Breadcrumbs, IconButton, Tooltip,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import BoltIcon from '@mui/icons-material/Bolt';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatCurrency } from '../utils/currency';
import { getImageUrl, API_BASE_URL } from '../utils/media';

const AuctionDetailPage = () => {
  const { id: auctionId } = useParams();
  const { token, user } = useContext(AuthContext);
  const socket = useSocket();

  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState('');
  const [maxBidAmount, setMaxBidAmount] = useState('');
  const [showMaxBid, setShowMaxBid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bidError, setBidError] = useState('');
  const [hasEnded, setHasEnded] = useState(false);
  const [sold, setSold] = useState(false);
  const [watching, setWatching] = useState(false);

  useEffect(() => {
    const fetchAuctionDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auctions/${auctionId}`);
        if (!response.ok) throw new Error('Failed to fetch auction details.');
        const data = await response.json();
        setAuction(data.auction);
        setBids(data.bids);
        setBidAmount((Number(data.auction.current_price) + 1).toFixed(2));
        setHasEnded(data.auction.status !== 'active' || new Date(data.auction.end_time) <= new Date());
        setSold(data.auction.status === 'sold');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAuctionDetails();
  }, [auctionId]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setWatching(list.some((a) => String(a.id) === String(auctionId))))
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/auctions/${auctionId}/my-max-bid`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data?.maxAmount) { setMaxBidAmount(data.maxAmount); setShowMaxBid(true); } })
      .catch(() => {});
  }, [token, auctionId]);

  const toggleWatch = async () => {
    if (!token) return setBidError('You must be logged in to watch an auction.');
    const method = watching ? 'DELETE' : 'POST';
    try {
      await fetch(`${API_BASE_URL}/api/watchlist/${auctionId}`, { method, headers: { Authorization: `Bearer ${token}` } });
      setWatching(!watching);
    } catch {
      // Non-critical — leave the star state as-is on failure.
    }
  };

  useEffect(() => {
    if (!socket) return;

    socket.emit('joinAuctionRoom', auctionId);

    const handleNewBid = (newBid) => {
      if (String(newBid.auction_id) !== String(auctionId)) return;
      setBids((prev) => [newBid, ...prev]);
      setAuction((prev) => (prev ? { ...prev, current_price: newBid.amount } : prev));
      setBidAmount((Number(newBid.amount) + 1).toFixed(2));
      setBidError('');
    };
    const handleBidError = ({ message }) => setBidError(message);
    const handleAuctionEnded = ({ id, sold: wasSold }) => {
      if (String(id) !== String(auctionId)) return;
      setHasEnded(true);
      setSold(Boolean(wasSold));
      setAuction((prev) => (prev ? { ...prev, status: wasSold ? 'sold' : 'ended' } : prev));
    };

    socket.on('newBid', handleNewBid);
    socket.on('bidError', handleBidError);
    socket.on('auctionEnded', handleAuctionEnded);
    return () => {
      socket.emit('leaveAuctionRoom', auctionId);
      socket.off('newBid', handleNewBid);
      socket.off('bidError', handleBidError);
      socket.off('auctionEnded', handleAuctionEnded);
    };
  }, [socket, auctionId]);

  const handleBidSubmit = (e) => {
    e.preventDefault();
    setBidError('');
    if (!user) return setBidError('You must be logged in to place a bid.');
    if (hasEnded) return setBidError('This auction has ended.');
    const maxAmount = showMaxBid && maxBidAmount ? Number(maxBidAmount) : undefined;
    if (maxAmount && maxAmount <= Number(bidAmount)) {
      return setBidError('Your max bid should be higher than your starting bid.');
    }
    socket.emit('placeBid', { auctionId, amount: Number(bidAmount), maxAmount, token });
  };

  const handleBuyNow = () => {
    setBidError('');
    if (!user) return setBidError('You must be logged in to buy this item.');
    if (!window.confirm(`Buy this item now for ${formatCurrency(auction.buy_now_price)}?`)) return;
    socket.emit('buyItNow', { auctionId, token });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!auction) return <Container sx={{ py: 4 }}><Typography>Auction not found.</Typography></Container>;

  const highestBid = bids[0];
  const isSeller = user && user.userId === auction.seller_id;
  const reservePrice = auction.reserve_price ? Number(auction.reserve_price) : null;
  const reserveMet = reservePrice ? Number(auction.current_price) >= reservePrice : true;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2, overflowX: 'auto', whiteSpace: 'nowrap', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
      >
        <Link to="/auctions" style={{ color: 'inherit', textDecoration: 'none' }}>Live Auctions</Link>
        <Typography color="text.primary">{auction.product_name}</Typography>
      </Breadcrumbs>
      {auction.category && (
        <Chip label={auction.subcategory ? `${auction.category} / ${auction.subcategory}` : auction.category} size="small" sx={{ mb: 1.5 }} />
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{auction.product_name}</Typography>
        {!isSeller && (
          <Tooltip title={watching ? 'Stop watching' : 'Watch this auction'}>
            <IconButton onClick={toggleWatch} color={watching ? 'primary' : 'default'}>
              {watching ? <VisibilityIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box component="img" src={getImageUrl(auction.imageUrl)} alt={auction.product_name}
            sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 420 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{auction.description}</Typography>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700}>{formatCurrency(auction.current_price)}</Typography>
              <Box sx={{ mb: 2 }}>
                {hasEnded ? (
                  <Chip label={sold ? 'Sold' : 'Auction Ended'} size="small" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ends in: <Countdown endTime={auction.end_time} onEnd={() => setHasEnded(true)} />
                  </Typography>
                )}
                {reservePrice && !hasEnded && (
                  <Chip
                    label={reserveMet ? 'Reserve met' : 'Reserve not yet met'}
                    color={reserveMet ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {hasEnded ? (
                <Box>
                  {sold && highestBid ? (
                    <Alert severity="success">Sold for {formatCurrency(highestBid.amount)} to User #{highestBid.user_id}</Alert>
                  ) : reservePrice && !reserveMet ? (
                    <Alert severity="warning">Auction ended — reserve price was not met, item did not sell.</Alert>
                  ) : highestBid ? (
                    <Alert severity="success">Winning bid: {formatCurrency(highestBid.amount)} by User #{highestBid.user_id}</Alert>
                  ) : (
                    <Alert severity="info">This auction ended with no bids.</Alert>
                  )}
                </Box>
              ) : isSeller ? (
                <Alert severity="info">You are the seller of this item and cannot bid on it.</Alert>
              ) : (
                <Stack spacing={2}>
                  {auction.buy_now_price && (
                    <Button
                      variant="contained" color="success" size="large" startIcon={<BoltIcon />}
                      onClick={handleBuyNow}
                    >
                      Buy It Now — {formatCurrency(auction.buy_now_price)}
                    </Button>
                  )}
                  <Box component="form" onSubmit={handleBidSubmit}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        type="number" size="small" fullWidth value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        inputProps={{ min: Number(auction.current_price) + 0.01, step: 0.01 }}
                        required
                      />
                      <Button type="submit" variant="contained" startIcon={<GavelIcon />}>Bid</Button>
                    </Stack>
                    {showMaxBid ? (
                      <TextField
                        type="number" size="small" fullWidth sx={{ mt: 1 }}
                        label="Auto-bid up to (optional)" value={maxBidAmount}
                        onChange={(e) => setMaxBidAmount(e.target.value)}
                        helperText="We'll automatically bid on your behalf, up to this amount, if you're outbid."
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    ) : (
                      <Button size="small" sx={{ mt: 1 }} onClick={() => setShowMaxBid(true)}>Set a max bid (auto-bid)</Button>
                    )}
                  </Box>
                </Stack>
              )}
              {bidError && <Alert severity="error" sx={{ mt: 2 }}>{bidError}</Alert>}
            </CardContent>
          </Card>

          <Typography variant="h6" fontWeight={700} gutterBottom>Bid History ({bids.length})</Typography>
          {bids.length === 0 ? (
            <Typography color="text.secondary">No bids yet. Be the first!</Typography>
          ) : (
            <List dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {bids.map((bid, idx) => (
                <React.Fragment key={bid.id}>
                  <ListItem sx={{ bgcolor: idx === 0 ? 'action.hover' : 'transparent', borderRadius: 1 }}>
                    <ListItemText
                      primary={formatCurrency(bid.amount)}
                      secondary={`User #${bid.user_id} · ${new Date(bid.timestamp).toLocaleTimeString()}`}
                    />
                  </ListItem>
                  {idx < bids.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuctionDetailPage;
